import React, { useEffect, useRef, useState } from 'react';
import { FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import L from 'leaflet';
import { createRoot } from 'react-dom/client';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectRTFO, setMapFeatures } from '../../reducers/rtfoSlice';
import { useSocket } from '../../context/useSocket';

const useStyles = makeStyles(theme => ({
  root: {
    width: 200,
    padding: 5,
    backgroundColor: '#fff'
    // border: '1px solid #ccc',
    // borderRadius: 5
  },
  popupFormInput: {
    width: '100%',
    marginBottom: 10,
    padding: 8,
    fontSize: 16,
    border: '1px solid #ccc',
    borderRadius: 4
  },
  popupFormColor: {
    width: '100%',
    marginBottom: 10,
    padding: 0
  },
  popupFormButton: {
    width: '100%',
    padding: 10,
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer'
  }
}));

const RTFOMapDrawingControl = () => {
  const map = useMap();
  const { socket } = useSocket();

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const dispatch = useDispatch();

  const featureGroupRef = useRef(L.featureGroup());

  // re-draw feature layers
  useEffect(() => {
    const featureGroup = featureGroupRef.current;

    const loadShapes = () => {
      try {
        // Load saved features from state
        const savedFeatures = currentRTFO?.mapFeatures || [];

        if (!savedFeatures?.length > 0) return;

        // Clear existing layers
        featureGroupRef.current.clearLayers();
        featureGroup.removeFrom(map);

        const loadedLayers = [];

        for (const feature of savedFeatures) {
          const { layerType, layer, name, color, isActive } = feature;

          if (!isActive) continue;
          let layerObj;

          switch (layerType) {
            case 'rectangle':
            case 'polygon':
            case 'polyline':
            case 'circle':
              layerObj = L[layerType](layer.latlngs, { color });
              break;
            case 'marker':
              layerObj = L.marker(layer.latlngs[0], { color });
              layerObj.bindPopup(`<div style="color: ${color};">${name}</div>`);
              break;
            default:
              return;
          }

          if (layerType !== 'marker') {
            const center = layerObj.getBounds().getCenter();
            const markerObj = L.marker(center, {
              icon: L.divIcon({
                className: 'custom-annotation',
                html: `<div style="color: ${color}; background: transparent; padding: 2px;">${name}</div>`
              })
            });
            featureGroup.addLayer(markerObj); // Add marker to feature group
          }

          loadedLayers.push(layerObj);
          featureGroup.addLayer(layerObj); // Add layer to feature group
        }

        // Add the feature group to the map
        if (!map.hasLayer(featureGroup)) {
          featureGroup.addTo(map);
        }
      } catch (error) {
        console.log(error);
      }
    };

    loadShapes();

    return () => featureGroup.removeFrom(map);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, currentRTFO?.mapFeatures]);

  const saveFeatures = newFeature => {
    if (!newFeature || socket?.readyState !== 1) return;

    dispatch((dispatch, getState) => {
      const state = getState();
      const currentFeatures = state?.rtfo?.value?.mapFeatures || [];
      const updatedFeatures = [...currentFeatures, newFeature];

      // Save features to the db
      socket.send(JSON.stringify({
        messageType: 'setMapFeatures',
        padName: currentRTFO?.name,
        mapFeatures: updatedFeatures.map(f => ({ ...f, isActive: false }))
      }))

      // Update the Redux state with the new features
      dispatch(setMapFeatures(updatedFeatures));
    });
  };

  const handleCreated = e => {
    const type = e.layerType;
    const layer = e.layer;

    const container = document.createElement('div');
    const root = createRoot(container);
    container.style.width = '200px';

    root.render(
      <PopupForm
        onSubmit={(name, color) => {
          // Update layer with name and color
          if (type !== 'marker') {
            layer.setStyle({ color });
            const center = layer.getBounds().getCenter();
            L.marker(center, {
              icon: L.divIcon({
                className: 'custom-annotation',
                html: `<div style="color: ${color}; background: transparent; padding: 2px;">${name}</div>`
              })
            }).addTo(map);
          } else {
            layer.bindPopup(`<div style="color: ${color};">${name}</div>`).openPopup();
          }

          const newFeature = {
            layerType: type,
            layer: { latlngs: layer.getLatLngs ? layer.getLatLngs() : [layer.getLatLng()] },
            name,
            color,
            isActive: true
          };

          saveFeatures(newFeature);

          layer.closePopup();
        }}
      />
    );

    layer.bindPopup(container).openPopup();
  };

  // const handleEdited = e => {
  //   const layers = e.layers;

  //   layers.eachLayer(layer => {
  //     const type =
  //       layer instanceof L.Marker
  //         ? 'marker'
  //         : layer instanceof L.Polygon
  //           ? 'polygon'
  //           : layer instanceof L.Polyline
  //             ? 'polyline'
  //             : layer instanceof L.Rectangle
  //               ? 'rectangle'
  //               : 'circle';

  //     const feature = currentRTFO?.mapFeatures?.find(f => {
  //       if (
  //         f.layer?.latlngs?.[0]?.length === layer['_latlngs']?.[0]?.length &&
  //         f.layer?.latlngs?.[0]?.[0]?.lat === layer['_latlngs']?.[0]?.[0]?.lat &&
  //         f.layer?.latlngs?.[0]?.[0]?.lng === layer['_latlngs']?.[0]?.[0]?.lng
  //       ) {
  //         return true;
  //       }
  //       return false;
  //     });

  //     if (feature) {
  //       const newFeature = {
  //         layerType: type,
  //         layer: { latlngs: layer.getLatLngs ? layer.getLatLngs() : [layer.getLatLng()] },
  //         name: feature?.name,
  //         color: feature?.color,
  //         isActive: feature?.isActive
  //       };

  //       saveFeatures(newFeature);
  //     }
  //   });
  // };

  // const handleDeleted = e => {
  //   const layers = e.layers;
  //   const features = JSON.parse(JSON.stringify(currentRTFO?.mapFeatures || []));

  //   layers.eachLayer(layer => {
  //     const featureIndex = features.findIndex(f => {
  //       if (
  //         f.layer?.latlngs?.[0]?.length === layer['_latlngs']?.[0]?.length &&
  //         f.layer?.latlngs?.[0]?.[0]?.lat === layer['_latlngs']?.[0]?.[0]?.lat &&
  //         f.layer?.latlngs?.[0]?.[0]?.lng === layer['_latlngs']?.[0]?.[0]?.lng
  //       ) {
  //         return true;
  //       }
  //       return false;
  //     });

  //     if (featureIndex !== -1) {
  //       features.splice(featureIndex, 1);
  //     }
  //   });

  //   // update local state
  //   dispatch(setMapFeatures(features));

  //   // delete feature from the db
  //   io?.connected &&
  //     io.emit('setMapFeatures', {
  //       padName: currentRTFO?.name,
  //       mapFeatures: features
  //     });
  // };

  return (
    <FeatureGroup ref={featureGroupRef}>
      <EditControl
        position="topleft"
        onCreated={handleCreated}
        // onEdited={handleEdited}
        // onDeleted={handleDeleted}
        edit={{
          edit: false,
          remove: false
        }}
        draw={{
          rectangle: {
            shapeOptions: {
              color: '#ff0000', // Adjust color as needed
              weight: 4
            },
            metric: false
          },
          polyline: false,
          polygon: {
            shapeOptions: {
              color: '#0000ff',
              weight: 4
            },
            metric: false
          },
          circle: false,
          marker: false,
          circlemarker: false
        }}
      />
    </FeatureGroup>
  );

  function PopupForm({ onSubmit }) {
    const classes = useStyles();

    const [name, setName] = useState('New feature');
    const [color, setColor] = useState('#ff0000');

    const handleSave = () => {
      if (name?.length > 0) {
        onSubmit(name, color);
        setName('');
      }
    };

    return (
      <div className={classes.root}>
        <label>
          Name:
          <input className={classes.popupFormInput} type="text" value={name} onChange={e => setName(e.target.value)} />
        </label>
        <label>
          Color:
          <input className={classes.popupFormColor} type="color" value={color} onChange={e => setColor(e.target.value)} />
        </label>
        <button className={classes.popupFormButton} type="button" onClick={handleSave} disabled={!name?.length}>
          Save
        </button>
      </div>
    );
  }
};

export default RTFOMapDrawingControl;
